import Phaser from 'phaser';

export type ProductionCharacter = 'yasemin' | 'fevzi' | 'gorkem';
export type CharacterDirection = 'down' | 'left' | 'right' | 'up';

interface AnimationDefinition {
  frames: number[];
  frameRate: number;
  repeat: number;
}

interface CharacterManifest {
  character: ProductionCharacter;
  animations: Record<string, AnimationDefinition>;
}

const CHARACTERS: ProductionCharacter[] = ['yasemin', 'fevzi', 'gorkem'];

// These source rows contain one clean right-facing cycle duplicated across
// both semantic halves. Mirror only the production Sprite for the opposite
// direction; the stable root and its UI siblings always remain upright.
const MIRRORED_LEFT_STATES: Partial<Record<ProductionCharacter, ReadonlySet<string>>> = {
  yasemin: new Set(['walk', 'carryWalk']),
  gorkem: new Set(['enterWalk', 'walk']),
};

export function preloadProductionCharacters(scene: Phaser.Scene): void {
  for (const character of CHARACTERS) {
    const base = `${import.meta.env.BASE_URL}assets/characters/${character}`;
    scene.load.spritesheet(`${character}-production`, `${base}/atlas.png`, { frameWidth: 192, frameHeight: 192 });
    scene.load.json(`${character}-manifest`, `${base}/manifest.json`);
  }
}

export function registerProductionCharacterAnimations(scene: Phaser.Scene): void {
  for (const character of CHARACTERS) {
    const manifest = scene.cache.json.get(`${character}-manifest`) as CharacterManifest | undefined;
    if (!manifest) continue;
    for (const [semanticKey, definition] of Object.entries(manifest.animations)) {
      const key = `${character}.${semanticKey}`;
      if (scene.anims.exists(key)) continue;
      scene.anims.create({
        key,
        frames: definition.frames.map((frame) => ({ key: `${character}-production`, frame })),
        frameRate: definition.frameRate,
        repeat: definition.repeat,
      });
    }
  }
}

export function createProductionCharacterSprite(
  scene: Phaser.Scene,
  character: ProductionCharacter,
  scale: number,
  baselineY: number,
  initialState: string,
): Phaser.GameObjects.Sprite {
  const sprite = scene.add.sprite(0, baselineY, `${character}-production`, 0)
    .setOrigin(0.5, 184 / 192)
    .setScale(scale);
  sprite.setData('productionCharacter', character).setData('artScale', scale);
  const initialKey = `${character}.${initialState}.down`;
  if (scene.anims.exists(initialKey)) sprite.play(initialKey);
  return sprite;
}

function availableKey(
  scene: Phaser.Scene,
  character: ProductionCharacter,
  state: string,
  direction: CharacterDirection,
): string | undefined {
  const candidates = [`${character}.${state}.${direction}`, `${character}.${state}.down`];
  return candidates.find((key) => scene.anims.exists(key));
}

function resolveDirectionalArt(
  scene: Phaser.Scene,
  character: ProductionCharacter,
  state: string,
  direction: CharacterDirection,
): { key?: string; flipX: boolean } {
  const mirrorLeft = direction === 'left' && MIRRORED_LEFT_STATES[character]?.has(state);
  return {
    key: availableKey(scene, character, state, mirrorLeft ? 'right' : direction),
    flipX: Boolean(mirrorLeft),
  };
}

export function setProductionCharacterState(
  container: Phaser.GameObjects.Container,
  state: string,
  direction?: CharacterDirection,
  ignoreIfPlaying = false,
): boolean {
  const sprite = container.getData('sprite') as Phaser.GameObjects.Sprite | undefined;
  const character = sprite?.getData('productionCharacter') as ProductionCharacter | undefined;
  if (!sprite || !character) return false;
  const resolvedDirection = direction ?? container.getData('direction') ?? 'down';
  const { key, flipX } = resolveDirectionalArt(container.scene, character, state, resolvedDirection);
  if (!key) return false;
  container.setData('characterState', state).setData('direction', resolvedDirection);
  sprite.setFlipX(flipX);
  if (!ignoreIfPlaying || sprite.anims.currentAnim?.key !== key) sprite.play(key, true);
  return true;
}

export function setProductionCharacterDirection(
  container: Phaser.GameObjects.Container,
  direction: CharacterDirection,
): boolean {
  const sprite = container.getData('sprite') as Phaser.GameObjects.Sprite | undefined;
  if (!sprite?.getData('productionCharacter')) return false;
  const state = container.getData('characterState') ?? 'idle';
  return setProductionCharacterState(container, state, direction, true);
}

export function directionFromVector(
  dx: number,
  dy: number,
  previous: CharacterDirection = 'down',
): CharacterDirection {
  const horizontal = Math.abs(dx);
  const vertical = Math.abs(dy);
  const hysteresis = 1.12;
  if ((previous === 'left' || previous === 'right') && horizontal * hysteresis >= vertical) return dx < 0 ? 'left' : 'right';
  if ((previous === 'up' || previous === 'down') && vertical * hysteresis >= horizontal) return dy < 0 ? 'up' : 'down';
  return horizontal > vertical ? (dx < 0 ? 'left' : 'right') : (dy < 0 ? 'up' : 'down');
}
